// src/hooks/useLibraryFilters.ts
import { useState, useMemo, useCallback } from 'react';
import { Capsule } from '../types/capsule';
import { getDifficultyCategory } from '../features/capsules/utils/capsuleFormat';

const DIFFICULTY_FILTERS = ['Beginner', 'Intermediate', 'Advanced'] as const;

interface UseLibraryFiltersReturn {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  activeFilters: string[];
  toggleFilter: (filter: string) => void;
  clearFilters: () => void;
  filteredCapsules: Capsule[];
  availableTags: string[];
}

function matchesSearch(capsule: Capsule, query: string): boolean {
  if (!query) return true;
  const lowerQuery = query.toLowerCase();
  const title = capsule.metadata.title?.toLowerCase() || '';
  const artist = capsule.metadata.artist?.toLowerCase() || '';
  return title.includes(lowerQuery) || artist.includes(lowerQuery);
}

function matchesFilters(capsule: Capsule, filters: string[]): boolean {
  if (filters.length === 0) return true;

  const difficultyCategory = getDifficultyCategory(capsule.metadata.difficulty);
  const capsuleTags = capsule.metadata.tags || [];

  return filters.some((filter) => {
    if (DIFFICULTY_FILTERS.includes(filter as typeof DIFFICULTY_FILTERS[number])) {
      return difficultyCategory === filter;
    }
    return capsuleTags.includes(filter);
  });
}

export function useLibraryFilters(capsules: Capsule[]): UseLibraryFiltersReturn {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);

  const toggleFilter = useCallback((filter: string) => {
    setActiveFilters((prev) =>
      prev.includes(filter)
        ? prev.filter((f) => f !== filter)
        : [...prev, filter]
    );
  }, []);

  const clearFilters = useCallback(() => {
    setActiveFilters([]);
  }, []);

  const availableTags = useMemo(() => {
    const tagSet = new Set<string>();
    capsules.forEach((capsule) => {
      capsule.metadata.tags?.forEach((tag) => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [capsules]);

  const filteredCapsules = useMemo(() => {
    return capsules.filter((capsule) =>
      matchesSearch(capsule, searchQuery) && matchesFilters(capsule, activeFilters)
    );
  }, [capsules, searchQuery, activeFilters]);

  return {
    searchQuery,
    setSearchQuery,
    activeFilters,
    toggleFilter,
    clearFilters,
    filteredCapsules,
    availableTags,
  };
}
